import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import clsx from "clsx";
import PropTypes from "prop-types";
import useLanguageChange from "../../hooks/useLanguageChange";
import { KEYBOARD_KEYS, SHAPE_TYPE } from "../../constants/Constants";
import GenerateMarkupMenuItems from "./GenerateMarkupMenuItems";
import MarkupFontDropdown from "./MarkupFontDropdown";
import ClearButton from "./ClearButton";
import ThicknessButton from "./ThicknessButton";
import UndoButton from "./UndoButton";
import RedoButton from "./RedoButton";
import ColorDropdown from "./ColorDropdown";
import SaveButton from "./SaveButton";
import ExitButton from "./ExitButton";
import { MARKUP_MENU_TYPE } from "../../constants/MarkupConstants";
import { isSilentObserverAvailable } from "../../shared/function";
import markupService from "../../services/MarkupService/markupService";

const MarkupMenubar = ({ fromMarkup, isARMarkup }) => {
    const localisedValues = useLanguageChange();
    const { selectedShapeType } = useSelector((state) => state.markup);
    const [numOfItems, setNumOfItems] = useState(0);
    const [breakWidths, setBreakWidths] = useState([]);
    const [availableSpace, setAvailableSpace] = useState(0);
    const [numOfVisibleItems, setNumOfVisibleItems] = useState(0);
    const [requiredSpace, setRequiredSpace] = useState(0);
    const [isHiddenLinksVisible, setIsHiddenLinksVisible] = useState(false);

    const menuItems = [
        { iconClass: "icon-arrow", shapeType: SHAPE_TYPE.ARROW, labelKey: "arrow" },
        { iconClass: "icon-rectangle", shapeType: SHAPE_TYPE.RECT, labelKey: "rect" },
        { iconClass: "icon-draw", shapeType: SHAPE_TYPE.FREEDRAW, labelKey: "draw" },
        { iconClass: "icon-text", shapeType: SHAPE_TYPE.TEXT, labelKey: "text" },
        { iconClass: "icon-blur", shapeType: SHAPE_TYPE.BLUR, labelKey: "mask_lable" },
    ];

    /*
        Handle to set the total visible menus number
        Handle the array of all menu width break point
    */
    useEffect(() => {
        if (fromMarkup === MARKUP_MENU_TYPE.UPLOAD_MARKUP) {
            const visibleManus = document.getElementById("markup-visible-links");
            const childrenWidths = Array.from(visibleManus.children).map((child) => child.offsetWidth);
            setNumOfItems(visibleManus.children.length);
            const breakWidthsArray = childrenWidths.reduce((acc, width, index) => {
                if (index > 0) {
                    acc.push(acc[index - 1] + width);
                } else {
                    acc.push(width);
                }
                return acc;
            }, []);
            setBreakWidths(breakWidthsArray);
        }
    }, []);

    /* Handle the more button is visible or not */
    useEffect(() => {
        if (fromMarkup === MARKUP_MENU_TYPE.UPLOAD_MARKUP) {
            const hiddenLinks = document.getElementById("markup-hidden-links");
            if (hiddenLinks.children.length > 0) {
                setIsHiddenLinksVisible(true);
            } else {
                setIsHiddenLinksVisible(false);
            }
        }
    }, [numOfVisibleItems]);

    useEffect(() => {
        if (fromMarkup === MARKUP_MENU_TYPE.UPLOAD_MARKUP) {
            const visibleLinks = document.getElementById("markup-visible-links");
            const hiddenLinks = document.getElementById("markup-hidden-links");

            const handleResize = () => {
                setAvailableSpace(visibleLinks.offsetWidth - 50);
                setNumOfVisibleItems(visibleLinks.children.length);
                setRequiredSpace(breakWidths[numOfVisibleItems - 1]);

                if (requiredSpace > availableSpace) {
                    const lastChild = visibleLinks?.lastElementChild;
                    hiddenLinks?.insertBefore(lastChild, hiddenLinks.firstChild);
                    setNumOfVisibleItems((prev) => prev - 1);
                } else if (availableSpace > breakWidths[numOfVisibleItems]) {
                    const firstChild = hiddenLinks?.firstElementChild;
                    visibleLinks?.appendChild(firstChild);
                    setNumOfVisibleItems((prev) => prev + 1);
                }

                if (numOfVisibleItems === numOfItems) {
                    document.getElementById("upload-file-markup-menu")?.classList.remove("align-markup-menu");
                } else {
                    document.getElementById("upload-file-markup-menu")?.classList.add("align-markup-menu");
                }

                const hiddenColorPicker = hiddenLinks.querySelector(".color-picker-box");
                if (hiddenColorPicker) {
                    hiddenColorPicker.addEventListener("click", function (e) {
                        /* Check if the target is a checkbox or radio button inside the color dropdown container */
                        const checkboxInput = e.target.closest('.color-picker-head-left .checkbox input[type="checkbox"]');
                        const radioInput = e.target.closest('.color-radiobox input[type="radio"]');

                        /* If a checkbox or radio input was clicked, handle accordingly */
                        if (checkboxInput || radioInput) {
                            return; /* Stop further event propagation for checkbox or radio */
                        }

                        /* If it's not a checkbox or radio button, stop the event propagation */
                        e.stopPropagation();
                    });
                }
            };

            window.addEventListener("resize", handleResize);

            handleResize();

            return () => {
                window.removeEventListener("resize", handleResize);
            };
        }
    }, [breakWidths, numOfItems, numOfVisibleItems, availableSpace, requiredSpace]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            const { key, code, keyCode, ctrlKey, shiftKey, metaKey } = e;

            const isEscapeKey = key === "Escape" || code === KEYBOARD_KEYS.ESCAPE || keyCode === KEYBOARD_KEYS.ESCAPE;
            const isCtrlZ = keyCode === KEYBOARD_KEYS.CTRLZ || code === KEYBOARD_KEYS.CTRLZ;
            const isCtrlY = keyCode === KEYBOARD_KEYS.CTRLY || code === KEYBOARD_KEYS.CTRLY;

            if (isEscapeKey) {
                markupService.handleOnEscapeKey();
            } else if (isCtrlZ && ctrlKey) {
                markupService.handleControlZkey();
            } else if (isCtrlY && ctrlKey) {
                markupService.handleControlYkey();
            } else if (isCtrlZ && shiftKey && metaKey) {
                // mac cmd + shift + z
                markupService.handleControlYkey();
            } else if (isCtrlZ && metaKey) {
                // mac cmd + z
                markupService.handleControlZkey();
            }
        };
        if (fromMarkup === MARKUP_MENU_TYPE.UPLOAD_MARKUP) {
            // Find the #color-picker-close-button element
            const colorPickerCloseButton = document.getElementById("color-picker-close-button");

            // Add click event listener to the #color-picker-close-button
            colorPickerCloseButton.addEventListener("click", function () {
                // Find the closest #color-picker-dropdown element
                document.getElementById("color-picker-dropdown").previousElementSibling.dispatchEvent(new Event("click", { bubbles: true }));
            });
        }
        if (fromMarkup === MARKUP_MENU_TYPE.MARKUP_ANNOTATION) {
            window.addEventListener("keydown", handleKeyDown);
        }
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    return (
        <div className='markup-menu'>
            <div className='markup-menu-in' id='markup-visible-links'>
                {!isSilentObserverAvailable() && (
                    <>
                        <ClearButton fromMarkup={fromMarkup} />
                        {!isARMarkup && (
                            <div className='markup-menu-item right-border markup-menu-item-thickness'>
                                <div className='markup-thickness-wrap'>
                                    {selectedShapeType !== SHAPE_TYPE.TEXT ? <ThicknessButton /> : <MarkupFontDropdown />}
                                </div>
                            </div>
                        )}
                        <UndoButton fromMarkup={fromMarkup} />
                        <RedoButton fromMarkup={fromMarkup} />
                        <ColorDropdown isARMarkup={isARMarkup} />
                        {GenerateMarkupMenuItems(menuItems, selectedShapeType, localisedValues, fromMarkup)}
                    </>
                )}
                {fromMarkup === MARKUP_MENU_TYPE.MARKUP_ANNOTATION && (
                    <>
                        <SaveButton />
                        {!isSilentObserverAvailable() && <ExitButton />}
                    </>
                )}
            </div>
            {fromMarkup === MARKUP_MENU_TYPE.UPLOAD_MARKUP && (
                <div className={clsx("dropdown upload-file-markup-moremenu", !isHiddenLinksVisible ? "d-none" : "")} id='markup-call-action-moremenu'>
                    <button
                        className='btn dropdown-toggle triangle-none moremenu-btn upload-file-markup-moremenu-btn'
                        type='button'
                        id='markup-call-action-dropdown'
                        data-bs-toggle='dropdown'
                        aria-expanded='false'
                    >
                        <span className='for-icon icon-more'></span>
                    </button>
                    <div
                        className='dropdown-menu upload-file-markup-dropdown right'
                        aria-labelledby='upload-file-markup-moremenu-btn'
                        id='markup-call-action-moremenu-dropdown'
                    >
                        <div className='dropdown-menu-in upload-file-markup-moremenu-hidden' id='markup-hidden-links'></div>
                    </div>
                </div>
            )}
        </div>
    );
};

MarkupMenubar.propTypes = {
    fromMarkup: PropTypes.string,
    isARMarkup: PropTypes.bool,
};

export default MarkupMenubar;
